import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts'
import { Users, GraduationCap, Briefcase, Church, BarChart3, PieChart, MapPin, TrendingUp, ArrowRight, Database } from 'lucide-react'
import { usePendudukStats } from '../hooks/use-penduduk-stats'
import { usePendidikanStats } from '../hooks/use-pendidikan-stats'
import { usePekerjaanStats } from '../hooks/use-pekerjaan-stats'
import { useAgamaStats } from '../hooks/use-agama-stats'
import { useDesilStats } from '../hooks/use-desil-stats'
import { useLatestDesil } from '../hooks/use-latest-desil'
import { Spinner } from './ui/spinner'

interface PublicAgama {
    nama: string
    jumlah: number
}

interface ClickableStatCardProps {
    title: string
    value: number
    icon: React.ReactNode
    color: 'blue' | 'green' | 'orange' | 'purple'
    subtitle: string
    onClick: () => void
}

interface ClickableCategoryChartProps {
    title: string
    data: PublicAgama[]
    icon: React.ReactNode
    color: 'green' | 'orange' | 'purple'
    onClick: () => void
}

interface InteractiveGenderPieChartProps {
    lakiLaki: number
    perempuan: number
    onClick: () => void
}

interface ClickableAgeBarChartProps {
    ageData: { range: string; jumlah: number }[]
    onClick: () => void
}

interface ClickableRegionalAnalysisProps {
    dusunData: { namaDusun: string; namaDesa?: string; jumlah: number }[]
    onClick: () => void
}

interface MultiCategoryComparisonProps {
    pendidikan: PublicAgama[]
    pekerjaan: PublicAgama[]
    agama: PublicAgama[]
}

function ClickableStatCard({ title, value, icon, color, subtitle, onClick }: ClickableStatCardProps) {
    const colorClasses = {
        blue: 'bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
        green: 'bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700',
        orange: 'bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700',
        purple: 'bg-gradient-to-br from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700'
    }

    return (
        <div 
            className={`${colorClasses[color]} text-white p-6 rounded-xl shadow-lg cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-xl group`}
            onClick={onClick}
        >
            <div className="flex items-center justify-between">
                <div className="text-white opacity-90 group-hover:opacity-100 transition-opacity">
                    {icon}
                </div>
                <ArrowRight className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="mt-4">
                <h3 className="text-sm font-medium opacity-90">{title}</h3>
                <p className="text-3xl font-bold mt-1">{value.toLocaleString('id-ID')}</p>
                <p className="text-sm opacity-80 mt-1">{subtitle}</p>
            </div>
        </div>
    )
}

function InteractiveGenderPieChart({ lakiLaki, perempuan, onClick }: InteractiveGenderPieChartProps) {
    const [hoveredSegment, setHoveredSegment] = useState<string | null>(null)
    
    const total = lakiLaki + perempuan
    const percentageLakiLaki = total > 0 ? (lakiLaki / total) * 100 : 0
    const percentagePerempuan = total > 0 ? (perempuan / total) * 100 : 0
    
    const radius = 80
    const center = 100
    const circumference = 2 * Math.PI * radius
    
    const lakiLakiArc = (percentageLakiLaki / 100) * circumference
    const perempuanArc = (percentagePerempuan / 100) * circumference
    
    return (
        <div 
            className="bg-white p-6 rounded-xl shadow-lg cursor-pointer hover:shadow-xl transition-all duration-300 group border hover:border-blue-300"
            onClick={onClick}
        >
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    <h3 className="font-semibold text-lg">Distribusi Gender</h3>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            
            <div className="flex flex-col lg:flex-row items-center justify-center lg:space-x-8 space-y-6 lg:space-y-0">
                <div className="relative flex-shrink-0 flex items-center justify-center">
                    <svg viewBox="0 0 200 200" className="transform -rotate-90 w-48 h-48 sm:w-52 sm:h-52 lg:w-56 lg:h-56">
                        {/* Background circle */}
                        <circle
                            cx={center}
                            cy={center}
                            r={radius}
                            stroke="#f3f4f6"
                            strokeWidth="20"
                            fill="transparent"
                        />
                        
                        {/* Laki-laki segment */}
                        <circle
                            cx={center}
                            cy={center}
                            r={radius}
                            stroke={hoveredSegment === 'laki-laki' ? "#1d4ed8" : "#3b82f6"}
                            strokeWidth="20"
                            fill="transparent"
                            strokeDasharray={`${lakiLakiArc} ${circumference}`}
                            strokeLinecap="round"
                            className="cursor-pointer transition-all duration-300"
                            onMouseEnter={() => setHoveredSegment('laki-laki')}
                            onMouseLeave={() => setHoveredSegment(null)}
                        />
                        
                        {/* Perempuan segment */}
                        <circle
                            cx={center}
                            cy={center}
                            r={radius}
                            stroke={hoveredSegment === 'perempuan' ? "#db2777" : "#ec4899"}
                            strokeWidth="20"
                            fill="transparent"
                            strokeDasharray={`${perempuanArc} ${circumference}`}
                            strokeDashoffset={-lakiLakiArc}
                            strokeLinecap="round"
                            className="cursor-pointer transition-all duration-300"
                            onMouseEnter={() => setHoveredSegment('perempuan')}
                            onMouseLeave={() => setHoveredSegment(null)}
                        />
                    </svg>
                    
                    {/* Center text - positioned absolutely to match SVG center */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-center">
                            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{total.toLocaleString('id-ID')}</p>
                            <p className="text-xs sm:text-sm text-gray-600">Total</p>
                        </div>
                    </div>
                </div>
                
                <div className="space-y-3 sm:space-y-4 w-full lg:w-auto min-w-[200px]">
                    <div 
                        className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-300 cursor-pointer ${
                            hoveredSegment === 'laki-laki' ? 'bg-blue-50 scale-105' : 'hover:bg-gray-50'
                        }`}
                        onMouseEnter={() => setHoveredSegment('laki-laki')}
                        onMouseLeave={() => setHoveredSegment(null)}
                    >
                        <div className="w-4 h-4 bg-blue-500 rounded-full flex-shrink-0"></div>
                        <div className="flex-1">
                            <p className="font-medium text-sm sm:text-base">Laki-laki</p>
                            <p className="text-xs sm:text-sm text-gray-600">{lakiLaki.toLocaleString('id-ID')} ({percentageLakiLaki.toFixed(1)}%)</p>
                        </div>
                    </div>
                    
                    <div 
                        className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-300 cursor-pointer ${
                            hoveredSegment === 'perempuan' ? 'bg-pink-50 scale-105' : 'hover:bg-gray-50'
                        }`}
                        onMouseEnter={() => setHoveredSegment('perempuan')}
                        onMouseLeave={() => setHoveredSegment(null)}
                    >
                        <div className="w-4 h-4 bg-pink-500 rounded-full flex-shrink-0"></div>
                        <div className="flex-1">
                            <p className="font-medium text-sm sm:text-base">Perempuan</p>
                            <p className="text-xs sm:text-sm text-gray-600">{perempuan.toLocaleString('id-ID')} ({percentagePerempuan.toFixed(1)}%)</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

function ClickableAgeBarChart({ ageData, onClick }: ClickableAgeBarChartProps) {
    const maxValue = Math.max(...ageData.map(item => item.jumlah))
    
    return (
        <div 
            className="bg-white p-6 rounded-xl shadow-lg cursor-pointer hover:shadow-xl transition-all duration-300 group border hover:border-green-300"
            onClick={onClick}
        >
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                    <BarChart3 className="h-5 w-5 text-green-600" />
                    <h3 className="font-semibold text-lg">Distribusi Usia</h3>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            
            <div className="space-y-3">
                {ageData.slice(0, 5).map((item, index) => (
                    <div key={index} className="flex items-center space-x-3">
                        <div className="w-20 text-sm font-medium text-gray-700">{item.range}</div>
                        <div className="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden">
                            <div 
                                className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full transition-all duration-1000 ease-out"
                                style={{ width: `${(item.jumlah / maxValue) * 100}%` }}
                            ></div>
                            <div className="absolute inset-0 flex items-center justify-end pr-2">
                                <span className="text-xs font-medium text-gray-700">{item.jumlah}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

function ClickableCategoryChart({ title, data, icon, color, onClick }: ClickableCategoryChartProps) {
    const colorClasses = {
        green: {
            border: 'hover:border-green-300',
            icon: 'text-green-600',
            gradient: 'from-green-400 to-green-600'
        },
        orange: {
            border: 'hover:border-orange-300',
            icon: 'text-orange-600',
            gradient: 'from-orange-400 to-orange-600'
        },
        purple: {
            border: 'hover:border-purple-300',
            icon: 'text-purple-600',
            gradient: 'from-purple-400 to-purple-600'
        }
    }
    
    const maxValue = Math.max(...data.map(item => item.jumlah))
    
    return (
        <div 
            className={`bg-white p-6 rounded-xl shadow-lg cursor-pointer hover:shadow-xl transition-all duration-300 group border ${colorClasses[color].border}`}
            onClick={onClick}
        >
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                    <div className={colorClasses[color].icon}>
                        {icon}
                    </div>
                    <h3 className="font-semibold text-lg">{title}</h3>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            
            <div className="space-y-3">
                {data.slice(0, 4).map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700 truncate flex-1 mr-2">{item.nama}</span>
                        <div className="flex items-center space-x-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div 
                                    className={`h-full bg-gradient-to-r ${colorClasses[color].gradient} rounded-full transition-all duration-1000 ease-out`}
                                    style={{ width: `${(item.jumlah / maxValue) * 100}%` }}
                                ></div>
                            </div>
                            <span className="text-sm font-semibold text-gray-900 w-8 text-right">{item.jumlah}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

function MultiCategoryComparison({ pendidikan, pekerjaan, agama }: MultiCategoryComparisonProps) {
    return (
        <div className="bg-white p-6 rounded-xl shadow-lg border">
            <div className="flex items-center space-x-2 mb-6">
                <TrendingUp className="h-5 w-5 text-indigo-600" />
                <h3 className="font-semibold text-lg">Analisis Komparatif</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{pendidikan.length}</div>
                    <div className="text-sm text-gray-600">Jenis Pendidikan</div>
                    <div className="text-xs text-gray-500 mt-1">
                        Terbanyak: {pendidikan[0]?.nama || 'N/A'}
                    </div>
                </div>
                
                <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{pekerjaan.length}</div>
                    <div className="text-sm text-gray-600">Jenis Pekerjaan</div>
                    <div className="text-xs text-gray-500 mt-1">
                        Terbanyak: {pekerjaan[0]?.nama || 'N/A'}
                    </div>
                </div>
                
                <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{agama.length}</div>
                    <div className="text-sm text-gray-600">Kepercayaan</div>
                    <div className="text-xs text-gray-500 mt-1">
                        Terbanyak: {agama[0]?.nama || 'N/A'}
                    </div>
                </div>
            </div>
        </div>
    )
}

// Threshold: jika dusun lebih dari nilai ini, gunakan bar chart; di bawah/sama, gunakan donut chart
const DONUT_THRESHOLD = 5
const TOP_N = 8

// Warna tetap untuk donut (max 5 segmen)
const DONUT_COLORS = [
    { stroke: '#3b82f6', hsl: 'hsl(217,91%,60%)' },
    { stroke: '#22c55e', hsl: 'hsl(142,71%,45%)' },
    { stroke: '#a855f7', hsl: 'hsl(271,91%,65%)' },
    { stroke: '#f97316', hsl: 'hsl(25,95%,53%)' },
    { stroke: '#ec4899', hsl: 'hsl(330,81%,60%)' },
]

function ClickableRegionalAnalysis({ dusunData, onClick }: ClickableRegionalAnalysisProps) {
    const [hoveredDusun, setHoveredDusun] = useState<string | null>(null)

    // Selalu urutkan dari terbanyak
    const sorted = [...dusunData].sort((a, b) => b.jumlah - a.jumlah)
    const total = sorted.reduce((sum, item) => sum + item.jumlah, 0)
    const maxValue = sorted[0]?.jumlah ?? 0
    const useBarChart = dusunData.length > DONUT_THRESHOLD

    // Tampilan donut untuk data ≤ DONUT_THRESHOLD
    const DonutChart = () => (
        <>
            <div className="flex items-center justify-center mb-6">
                <div className="relative w-56 h-56">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="35" fill="none" stroke="#f3f4f6" strokeWidth="8" />
                        {sorted.map((item, index) => {
                            const circumference = 2 * Math.PI * 35
                            const arc = (item.jumlah / total) * circumference
                            const offset = -sorted.slice(0, index).reduce((sum, prev) => {
                                return sum + (prev.jumlah / total) * circumference
                            }, 0)
                            const color = DONUT_COLORS[index % DONUT_COLORS.length]
                            return (
                                <circle
                                    key={index}
                                    cx="50" cy="50" r="35"
                                    fill="none"
                                    stroke={color.stroke}
                                    strokeWidth={hoveredDusun === item.namaDusun ? '11' : '8'}
                                    strokeDasharray={`${arc} ${circumference}`}
                                    strokeDashoffset={offset}
                                    opacity={hoveredDusun && hoveredDusun !== item.namaDusun ? 0.35 : 1}
                                    className="transition-all duration-300 cursor-pointer"
                                    onMouseEnter={() => setHoveredDusun(item.namaDusun)}
                                    onMouseLeave={() => setHoveredDusun(null)}
                                />
                            )
                        })}
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-center">
                            {hoveredDusun ? (
                                <>
                                    <p className="text-xs font-semibold text-indigo-600 px-2 line-clamp-2 leading-tight">{hoveredDusun}</p>
                                    <p className="text-xl font-bold text-gray-900 mt-1">
                                        {sorted.find(d => d.namaDusun === hoveredDusun)?.jumlah.toLocaleString('id-ID')}
                                    </p>
                                    <p className="text-xs text-gray-500">jiwa</p>
                                </>
                            ) : (
                                <>
                                    <p className="text-2xl font-bold text-gray-900">{total.toLocaleString('id-ID')}</p>
                                    <p className="text-xs text-gray-500">total jiwa</p>
                                    <p className="text-xs text-indigo-500 font-medium mt-1">{dusunData.length} dusun</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="space-y-2">
                {sorted.map((item, index) => {
                    const color = DONUT_COLORS[index % DONUT_COLORS.length]
                    const pct = total > 0 ? (item.jumlah / total) * 100 : 0
                    return (
                        <div
                            key={index}
                            className={`flex items-center space-x-3 p-2 rounded-lg transition-all duration-300 cursor-pointer ${
                                hoveredDusun === item.namaDusun ? 'bg-indigo-50' : 'hover:bg-gray-50'
                            }`}
                            onMouseEnter={() => setHoveredDusun(item.namaDusun)}
                            onMouseLeave={() => setHoveredDusun(null)}
                        >
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color.stroke }}></div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                    <div className="min-w-0">
                                        <span className="text-sm font-medium text-gray-700 truncate block">{item.namaDusun}</span>
                                        {item.namaDesa && (
                                            <span className="text-xs text-gray-400 truncate block">{item.namaDesa}</span>
                                        )}
                                    </div>
                                    <span className="text-sm font-bold text-gray-900 ml-2">{item.jumlah.toLocaleString('id-ID')}</span>
                                </div>
                                <div className="flex items-center space-x-2 mt-1">
                                    <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                        <div
                                            className="h-full rounded-full transition-all duration-500"
                                            style={{ width: `${pct}%`, backgroundColor: color.stroke }}
                                        ></div>
                                    </div>
                                    <span className="text-xs text-gray-500 w-10 text-right">{pct.toFixed(1)}%</span>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        </>
    )

    // Tampilan bar chart horizontal ranked untuk data > DONUT_THRESHOLD
    const barChartData = sorted.slice(0, TOP_N).map((item) => ({
        name: item.namaDusun,
        namaDesa: item.namaDesa,
        jumlah: item.jumlah,
        pct: total > 0 ? Number(((item.jumlah / total) * 100).toFixed(1)) : 0,
    }))

    const BarChartRanked = () => (
        <div>
            <p className="text-xs text-gray-500 mb-4">
                Menampilkan {barChartData.length} dusun teratas dari {dusunData.length} dusun
            </p>
            {/* Vertical bar chart - Recharts */}
            <div className="w-full h-[320px] mb-6">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barChartData} margin={{ top: 16, right: 16, left: 0, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                            dataKey="name"
                            angle={-40}
                            textAnchor="end"
                            height={60}
                            interval={0}
                            fontSize={11}
                        />
                        <YAxis fontSize={11} />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: 'white',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                                fontSize: '12px',
                            }}
                            formatter={(value: number) => {
                                return [`${value.toLocaleString('id-ID')} jiwa`, 'Jumlah']
                            }}
                            labelFormatter={(label: string) => {
                                const item = barChartData.find(d => d.name === label)
                                return item?.namaDesa ? `${label} — ${item.namaDesa}` : label
                            }}
                        />
                        <Bar
                            dataKey="jumlah"
                            fill="#6366f1"
                            radius={[4, 4, 0, 0]}
                            name="Jumlah Penduduk"
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* 3-column grid detail */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {sorted.slice(0, TOP_N).map((item, index) => {
                    const pct = total > 0 ? ((item.jumlah / total) * 100).toFixed(1) : '0'
                    return (
                        <div
                            key={index}
                            className={`p-2.5 rounded-lg border transition-all duration-200 cursor-pointer text-center ${
                                hoveredDusun === item.namaDusun
                                    ? 'bg-indigo-50 border-indigo-300'
                                    : 'bg-gray-50 border-gray-100 hover:bg-gray-100'
                            }`}
                            onMouseEnter={() => setHoveredDusun(item.namaDusun)}
                            onMouseLeave={() => setHoveredDusun(null)}
                        >
                            <p className="text-xs font-semibold text-gray-800 truncate">{item.namaDusun}</p>
                            {item.namaDesa && (
                                <p className="text-xs text-gray-400 truncate">{item.namaDesa}</p>
                            )}
                            <p className="text-sm font-bold text-indigo-600 mt-1">{item.jumlah.toLocaleString('id-ID')}</p>
                            <p className="text-xs text-gray-500">{pct}%</p>
                        </div>
                    )
                })}
            </div>

            {dusunData.length > TOP_N && (
                <div className="text-center mt-3">
                    <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                        +{dusunData.length - TOP_N} dusun lainnya — klik untuk lihat semua
                    </span>
                </div>
            )}
        </div>
    )

    return (
        <div
            className="bg-white p-6 rounded-xl shadow-lg cursor-pointer hover:shadow-xl transition-all duration-300 group border hover:border-indigo-300"
            onClick={onClick}
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-2">
                    {useBarChart
                        ? <MapPin className="h-5 w-5 text-indigo-600" />
                        : <PieChart className="h-5 w-5 text-indigo-600" />
                    }
                    <h3 className="font-semibold text-lg">Sebaran Penduduk per Dusun</h3>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <p className="text-xs text-gray-500 mb-5">
                {dusunData.length} dusun dalam wilayah desa/kecamatan ini
            </p>

            {/* Conditional chart */}
            {useBarChart ? <BarChartRanked /> : <DonutChart />}

            {/* Summary footer */}
            <div className="mt-4 p-3 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-100">
                <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                        <div className="text-lg font-bold text-indigo-600">{total.toLocaleString('id-ID')}</div>
                        <div className="text-xs text-indigo-500">Total Penduduk</div>
                    </div>
                    <div>
                        <div className="text-lg font-bold text-blue-600">{(sorted[0]?.jumlah ?? 0).toLocaleString('id-ID')}</div>
                        <div className="text-xs text-blue-500">Terbanyak</div>
                    </div>
                    <div>
                        <div className="text-lg font-bold text-purple-600">{Math.round(total / (dusunData.length || 1)).toLocaleString('id-ID')}</div>
                        <div className="text-xs text-purple-500">Rata-rata</div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export function ComprehensiveStatsDashboard() {
    const navigate = useNavigate()
    
    const { data: penduduk, isLoading: loadingPenduduk } = usePendudukStats()
    const { data: pendidikan, isLoading: loadingPendidikan } = usePendidikanStats()
    const { data: pekerjaan, isLoading: loadingPekerjaan } = usePekerjaanStats()
    const { data: agama, isLoading: loadingAgama } = useAgamaStats()
    const { data: desil, isLoading: loadingDesil } = useDesilStats()
    const { data: latestDesil, isLoading: loadingLatestDesil } = useLatestDesil()
    
    const isLoading = loadingPenduduk || loadingPendidikan || loadingPekerjaan || loadingAgama || loadingDesil || loadingLatestDesil

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="text-center">
                    <Spinner className="h-12 w-12 mx-auto mb-4" />
                    <h3 className="text-lg font-medium">Memuat Dashboard Statistik</h3>
                    <p className="text-muted-foreground">Mengintegrasikan semua data...</p>
                </div>
            </div>
        )
    }

    const pendudukData = penduduk?.data
    const pendidikanData = pendidikan?.data
    const pekerjaanData = pekerjaan?.data
    const agamaData = agama?.data
    const desilData = desil?.data
    const latestDesilData = latestDesil?.data

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h2 className="text-2xl font-bold text-gray-900">Dashboard Komprehensif</h2>
                <p className="text-gray-600">Overview lengkap statistik demografis dan sosial ekonomi</p>
            </div>

            {/* Key Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                <ClickableStatCard 
                    title="Total Penduduk"
                    value={pendudukData?.totalPenduduk || 0}
                    icon={<Users className="h-6 w-6" />}
                    color="blue"
                    subtitle="Jumlah seluruh warga"
                    onClick={() => navigate('/statistics/penduduk')}
                />
                <ClickableStatCard 
                    title="Tingkat Pendidikan"
                    value={pendidikanData?.pendidikan?.filter(item => item.jumlah > 0)?.length || 0}
                    icon={<GraduationCap className="h-6 w-6" />}
                    color="green"
                    subtitle="Kategori pendidikan yang terisi"
                    onClick={() => navigate('/statistics/pendidikan')}
                />
                <ClickableStatCard 
                    title="Jenis Pekerjaan"
                    value={pekerjaanData?.pekerjaan?.filter(item => item.jumlah > 0)?.length || 0}
                    icon={<Briefcase className="h-6 w-6" />}
                    color="orange"
                    subtitle="Variasi pekerjaan yang terisi"
                    onClick={() => navigate('/statistics/pekerjaan')}
                />
                <ClickableStatCard 
                    title="Keberagaman Agama"
                    value={agamaData?.agama?.filter(item => item.jumlah > 0)?.length || 0}
                    icon={<Church className="h-6 w-6" />}
                    color="purple"
                    subtitle="Agama yang dianut penduduk"
                    onClick={() => navigate('/statistics/agama')}
                />
                <ClickableStatCard 
                    title="Data Desil"
                    value={desilData?.totalRecords || 0}
                    icon={<Database className="h-6 w-6" />}
                    color="blue"
                    subtitle="Total data kesejahteraan"
                    onClick={() => navigate('/desil-statistics')}
                />
            </div>

            {/* Main Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Enhanced Gender Distribution Pie Chart */}
                <InteractiveGenderPieChart 
                    lakiLaki={pendudukData?.jumlahLakiLaki || 0}
                    perempuan={pendudukData?.jumlahPerempuan || 0}
                    onClick={() => navigate('/statistics/penduduk')}
                />

                {/* Age Distribution Bar Chart */}
                <ClickableAgeBarChart 
                    ageData={(pendudukData?.rangeUmur || []).map(item => ({ range: item.range, jumlah: item.jumlah }))}
                    onClick={() => navigate('/statistics/penduduk')}
                />
            </div>

            {/* Kepala Keluarga Chart */}
            {pendudukData && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Data Kepala Keluarga</h3>
                            <p className="text-sm text-gray-600">Perbandingan kepala keluarga dengan total penduduk</p>
                        </div>
                        <Users className="h-6 w-6 text-blue-600" />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="text-center">
                            <div className="text-3xl font-bold text-blue-600">{pendudukData.totalKepalaKeluarga}</div>
                            <div className="text-sm text-gray-600">Total Kepala Keluarga</div>
                        </div>
                        <div className="text-center">
                            <div className="text-3xl font-bold text-green-600">{pendudukData.persentaseKepalaKeluarga.toFixed(1)}%</div>
                            <div className="text-sm text-gray-600">Persentase dari Total Penduduk</div>
                        </div>
                        <div className="text-center">
                            <div className="text-3xl font-bold text-gray-600">{pendudukData.totalPenduduk - pendudukData.totalKepalaKeluarga}</div>
                            <div className="text-sm text-gray-600">Anggota Keluarga Lainnya</div>
                        </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-6">
                        <div className="flex justify-between text-sm text-gray-600 mb-2">
                            <span>Kepala Keluarga</span>
                            <span>Anggota Keluarga</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-4">
                            <div 
                                className="bg-gradient-to-r from-blue-500 to-blue-600 h-4 rounded-full transition-all duration-500"
                                style={{ width: `${pendudukData.persentaseKepalaKeluarga}%` }}
                            ></div>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>{pendudukData.persentaseKepalaKeluarga.toFixed(1)}%</span>
                            <span>{(100 - pendudukData.persentaseKepalaKeluarga).toFixed(1)}%</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Education, Occupation, Religion Charts */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                <ClickableCategoryChart 
                    title="Distribusi Pendidikan"
                    data={pendidikanData?.pendidikan || []}
                    icon={<GraduationCap className="h-5 w-5" />}
                    color="green"
                    onClick={() => navigate('/statistics/pendidikan')}
                />
                
                <ClickableCategoryChart 
                    title="Distribusi Pekerjaan"
                    data={pekerjaanData?.pekerjaan || []}
                    icon={<Briefcase className="h-5 w-5" />}
                    color="orange"
                    onClick={() => navigate('/statistics/pekerjaan')}
                />
                
                <ClickableCategoryChart 
                    title="Distribusi Agama"
                    data={agamaData?.agama || []}
                    icon={<Church className="h-5 w-5" />}
                    color="purple"
                    onClick={() => navigate('/statistics/agama')}
                />
            </div>

            {/* Comprehensive Comparison Chart */}
            <MultiCategoryComparison 
                pendidikan={pendidikanData?.pendidikan || []}
                pekerjaan={pekerjaanData?.pekerjaan || []}
                agama={agamaData?.agama || []}
            />

            {/* Regional Analysis */}
            {pendudukData?.perDusun && (
                <ClickableRegionalAnalysis 
                    // `namaDusun` NULLABLE di kontrak (baris tanpa dusun terdaftar). Diberi label
                    // eksplisit di sini alih-alih diteruskan null ke komponen yang menuntut string.
                    dusunData={pendudukData.perDusun.map(item => ({ namaDusun: item.namaDusun ?? 'Tanpa Dusun', namaDesa: item.namaDesa, jumlah: item.jumlah }))}
                    onClick={() => navigate('/statistics/penduduk')}
                />
            )}

            {/* Desil Kesejahteraan Preview */}
            {latestDesilData && (
                <div 
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-xl transition-all duration-300 group hover:border-blue-300"
                    onClick={() => navigate('/desil-statistics')}
                >
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Data Desil Kesejahteraan Terbaru</h3>
                            <p className="text-sm text-gray-600">Tahun {latestDesilData.tahun} - Tahap {latestDesilData.tahap}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Database className="h-6 w-6 text-blue-600" />
                            <ArrowRight className="h-5 w-5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                    </div>
                    
                    {/* Key Metrics Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                        <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                            <div className="text-2xl font-bold text-emerald-700">{latestDesilData.jumlahKpm}</div>
                            <div className="text-xs text-emerald-600 font-medium">Jumlah KPM</div>
                        </div>
                        <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="text-2xl font-bold text-blue-700">{latestDesilData.total}</div>
                            <div className="text-xs text-blue-600 font-medium">Total Keluarga</div>
                        </div>
                        <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                            <div className="text-2xl font-bold text-purple-700">{latestDesilData.tahun}</div>
                            <div className="text-xs text-purple-600 font-medium">Tahun Data</div>
                        </div>
                    </div>

                    {/* Desil Distribution Preview */}
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
                        <div className="text-center p-3 bg-red-50 rounded border border-red-200">
                            <div className="text-xs text-red-700 font-medium">Desil 1</div>
                            <div className="text-lg font-bold text-red-800">{latestDesilData.desil1}</div>
                        </div>
                        <div className="text-center p-3 bg-orange-50 rounded border border-orange-200">
                            <div className="text-xs text-orange-700 font-medium">Desil 2</div>
                            <div className="text-lg font-bold text-orange-800">{latestDesilData.desil2}</div>
                        </div>
                        <div className="text-center p-3 bg-yellow-50 rounded border border-yellow-200">
                            <div className="text-xs text-yellow-700 font-medium">Desil 3</div>
                            <div className="text-lg font-bold text-yellow-800">{latestDesilData.desil3}</div>
                        </div>
                        <div className="text-center p-3 bg-blue-50 rounded border border-blue-200">
                            <div className="text-xs text-blue-700 font-medium">Desil 4</div>
                            <div className="text-lg font-bold text-blue-800">{latestDesilData.desil4}</div>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded border border-green-200">
                            <div className="text-xs text-green-700 font-medium">Desil 5</div>
                            <div className="text-lg font-bold text-green-800">{latestDesilData.desil5}</div>
                        </div>
                        <div className="text-center p-3 bg-emerald-50 rounded border border-emerald-200">
                            <div className="text-xs text-emerald-700 font-medium">Desil 6-10</div>
                            <div className="text-lg font-bold text-emerald-800">{latestDesilData.desil6_10}</div>
                        </div>
                    </div>
                    
                    {/* Belum Diperingkat */}
                    {latestDesilData.belumDiperingkat > 0 && (
                        <div className="mb-4">
                            <div className="text-center p-3 bg-amber-50 rounded border border-amber-200">
                                <div className="text-xs text-amber-700 font-medium">Belum Diperingkat</div>
                                <div className="text-lg font-bold text-amber-800">{latestDesilData.belumDiperingkat}</div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
